import { useState, useEffect, useCallback } from 'react';
import { HISTORY_STORAGE_KEY, MAX_HISTORY_ITEMS } from '../utils/constants';
import { CalculatorState, CalculationResult } from './useOffsetCalculator';

export interface HistoryEntry {
  id: string;
  timestamp: number;
  state: CalculatorState;
  result: CalculationResult;
}

export function useHistory() {
  const [history, setHistory] = useState<HistoryEntry[]>([]);

  // Load history from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(HISTORY_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          setHistory(parsed);
        }
      }
    } catch (e) {
      console.error('Failed to load history:', e);
    }
  }, []);

  // Save history to localStorage when it changes
  useEffect(() => {
    try {
      localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(history));
    } catch (e) {
      console.error('Failed to save history:', e);
    }
  }, [history]);

  const addEntry = useCallback((state: CalculatorState, result: CalculationResult) => {
    const entry: HistoryEntry = {
      id: crypto.randomUUID(),
      timestamp: Date.now(),
      state,
      result
    };

    setHistory(prev => {
      const newHistory = [entry, ...prev];
      // Keep only the most recent entries
      return newHistory.slice(0, MAX_HISTORY_ITEMS);
    });
  }, []);

  const removeEntry = useCallback((id: string) => {
    setHistory(prev => prev.filter(entry => entry.id !== id));
  }, []);

  const clearHistory = useCallback(() => {
    setHistory([]);
  }, []);

  return {
    history,
    addEntry,
    removeEntry,
    clearHistory
  };
}

export function formatHistoryTimestamp(timestamp: number): string {
  const date = new Date(timestamp);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
}
